import React, { Fragment } from 'react';
import { themeGet } from 'styled-system';
import PropTypes from 'prop-types';
import { Flex, Box, Heading } from '@qga/roo-ui/components';
import styled from '@emotion/styled';
import PulsingLoadingBlock from 'components/PulsingLoadingBlock';
import { mediaQuery } from 'lib/styledSystem';
import { useSelector } from 'react-redux';
import {
  getHasValidQuery,
  getIsLoading as getIsLoadingStandard,
  getTotalRoomTypeCount,
  getAvailableRoomTypeCount,
} from 'store/propertyAvailability/propertyAvailabilitySelectors';
import { getIsExclusive } from 'store/property/propertySelectors';
import {
  getIsLoading as getIsLoadingExclusive,
  getExclusiveOfferAvailabilityCount,
  getExclusiveOfferRoomTypeCount,
} from 'store/exclusiveOffer/exclusiveOfferSelectors';

const SummaryLoader = styled(Flex)`
  height: ${themeGet('space.6')};
  transition: all 0.2s ease-in-out 0.2s;
  opacity: ${(props) => (props.isLoading ? '1' : '0')};
  align-items: center;
  margin-top: -24px;
`;

const SummaryResults = styled(Flex)`
  height: ${themeGet('space.4')};
  transition: all 0.2s ease-in-out 0.4s;
  opacity: ${(props) => (props.isLoading ? '0' : '1')};
  align-items: center;
  font-size: ${themeGet('fontSizes.sm')};
`;

const Summary = ({ isLoading, availableRoomTypeCount, totalRoomTypeCount, smallTablet }) => {
  const roomsShownCopy = `${availableRoomTypeCount} out of ${totalRoomTypeCount} rooms available`;

  return (
    <Fragment>
      <Flex flexDirection="column" alignItems="start" mb={smallTablet ? 4 : [4, 4, 8]} mt={0}>
        <SummaryResults isLoading={isLoading}>
          <Box data-testid="available-room-type-count">{roomsShownCopy}</Box>
          <Box>&nbsp;for your stay</Box>
        </SummaryResults>
        <SummaryLoader isLoading={isLoading}>
          <PulsingLoadingBlock height={14} width={50} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={20} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={100} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={20} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={100} mb={0} />
        </SummaryLoader>
      </Flex>
    </Fragment>
  );
};

Summary.propTypes = {
  availableRoomTypeCount: PropTypes.number,
  totalRoomTypeCount: PropTypes.number,
  isLoading: PropTypes.bool,
  smallTablet: PropTypes.bool,
};

Summary.defaultProps = {
  availableRoomTypeCount: 0,
  totalRoomTypeCount: 0,
  isLoading: false,
  smallTablet: false,
};

const SummaryContainer = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  min-height: 20px;
  text-align: center;
  flex-direction: column;
  align-content: flex-start;
  ${mediaQuery.minWidth.sm} {
    padding-top: 0px;
    min-height: 60px;
  }
`;

SummaryContainer.displayName = 'SummaryContainer';

const PropertyAvailabilitySummary = ({ smallTablet = false }) => {
  const hasQuery = useSelector(getHasValidQuery);
  const isExclusive = useSelector(getIsExclusive);
  const isLoadingStandard = useSelector(getIsLoadingStandard);
  const isLoadingExclusive = useSelector(getIsLoadingExclusive);
  const totalRoomTypeCountStandard = useSelector(getTotalRoomTypeCount);
  const totalRoomTypeCountExclusive = useSelector(getExclusiveOfferRoomTypeCount);
  const availableRoomTypeCountStandard = useSelector(getAvailableRoomTypeCount);
  const availableRoomTypeCountExclusive = useSelector(getExclusiveOfferAvailabilityCount);
  const isLoading = isExclusive ? isLoadingExclusive : isLoadingStandard;
  const totalRoomsCount = isExclusive ? totalRoomTypeCountExclusive : totalRoomTypeCountStandard;
  const availableRoomsCount = isExclusive ? availableRoomTypeCountExclusive : availableRoomTypeCountStandard;
  const showChooseRoomMessage = isLoading || !hasQuery;
  return (
    <Box id="view-rooms" backgroundColor="greys.porcelain" pt={[6, 0, 2]}>
      <SummaryContainer pl={[4, 6, 0]}>
        {showChooseRoomMessage || availableRoomsCount > 0 ? (
          <Heading.h2 data-testid="property-availability-header" fontSize={['md', 'lg']} fontWeight={400}>
            Choose your room
          </Heading.h2>
        ) : (
          <Heading.h2 data-testid="property-availability-header" fontSize={['md', 'lg']} fontWeight={400} pt={4}>
            We couldn&apos;t find any available rooms
          </Heading.h2>
        )}
        {hasQuery && (
          <Summary
            isLoading={isLoading}
            availableRoomTypeCount={availableRoomsCount}
            totalRoomTypeCount={totalRoomsCount}
            smallTablet={smallTablet}
          />
        )}
      </SummaryContainer>
    </Box>
  );
};

PropertyAvailabilitySummary.propTypes = {
  smallTablet: PropTypes.bool,
};

export default PropertyAvailabilitySummary;
